import { ReactElement, useCallback, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import Box from "ds/components/Box";
import FullScreenModal from "ds/components/FullScreenModal";
import { showSimpleLeaveConfirmation } from "ds/components/LeaveConfirmationModal/Simple";
import useCanCreateScan from "shared/Scan/access/useCanCreateScan";
import { getDefaultFormValues, mapFormValuesToScanInput } from "shared/Scan/helpers";
import { ScanFormFields } from "shared/Scan/types";
import useScanFormOptions from "shared/Scan/useScanFormOptions";

import { SCHEDULE_SCAN_WIZARD_STEPS_ORDER } from "./constants";
import { NewScanScheduleContext } from "./context";
import NewScanScheduleFooter from "./Footer";
import useScanCreate from "./hooks/useScanCreate";
import ScheduleScanWizardStepper from "./Stepper";
import ScheduleScanWizardStepAttachProviders from "./Steps/AttachProviders";
import ScheduleScanWizardStepAttachWorkerPool from "./Steps/StepAttachWorkerPool";
import ScheduleScanWizardStepDefineScope from "./Steps/DefineScope";
import ScheduleScanWizardStepScanDetails from "./Steps/StepScanDetails";
import ScheduleScanWizardStepSummary from "./Steps/StepSummary";
import { NewScanScheduleStep } from "./types";

const STEP_COMPONENTS: Record<NewScanScheduleStep, () => ReactElement> = {
  [NewScanScheduleStep.ScanDetails]: ScheduleScanWizardStepScanDetails,
  [NewScanScheduleStep.AttachProviders]: ScheduleScanWizardStepAttachProviders,
  [NewScanScheduleStep.AttachWorkerPool]: ScheduleScanWizardStepAttachWorkerPool,
  [NewScanScheduleStep.DefineScope]: ScheduleScanWizardStepDefineScope,
  [NewScanScheduleStep.Summary]: ScheduleScanWizardStepSummary,
};

const NewScanSchedule = () => {
  const navigate = useNavigate();
  const canCreateScan = useCanCreateScan();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = SCHEDULE_SCAN_WIZARD_STEPS_ORDER[currentStepIndex];

  const form = useForm<ScanFormFields>({
    defaultValues: getDefaultFormValues(),
    mode: "onChange",
  });

  const space = useWatch({ control: form.control, name: "space" });
  const formOptions = useScanFormOptions(space);

  const [createScan, { loading: isCreating }] = useScanCreate();

  const goToStep = useCallback((step: NewScanScheduleStep) => {
    setCurrentStepIndex(SCHEDULE_SCAN_WIZARD_STEPS_ORDER.indexOf(step));
  }, []);

  const goToNextStep = useCallback(() => {
    setCurrentStepIndex((index) =>
      Math.min(index + 1, SCHEDULE_SCAN_WIZARD_STEPS_ORDER.length - 1)
    );
  }, []);

  const goToPreviousStep = useCallback(() => {
    setCurrentStepIndex((index) => Math.max(index - 1, 0));
  }, []);

  const contextValue = useMemo(
    () => ({
      currentStep,
      currentStepIndex,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      formOptions,
    }),
    [currentStep, currentStepIndex, goToStep, goToNextStep, goToPreviousStep, formOptions]
  );

  const handleClose = useCallback(async () => {
    if (form.formState.isDirty) {
      await showSimpleLeaveConfirmation({
        title: "Do you want to leave scan scheduling?",
        message: "Your changes will not be saved.",
      });
    }

    navigate("/resources/scans");
  }, [form.formState.isDirty, navigate]);

  const handleFinish = form.handleSubmit((formValues) => {
    const input = mapFormValuesToScanInput(formValues);

    createScan({ input }, (data) => {
      if (data?.scanCreate) {
        navigate(`/resources/scans/${data.scanCreate.id}`);
      }
    });
  });

  if (!canCreateScan.result) {
    return <NotFoundPage />;
  }

  const StepComponent = STEP_COMPONENTS[currentStep];

  return (
    <FormProvider {...form}>
      <NewScanScheduleContext.Provider value={contextValue}>
        <FullScreenModal title="Create scan" onClose={handleClose}>
          <Box direction="row" grow="1" fullWidth overflowHidden>
            <ScheduleScanWizardStepper />

            <Box direction="column" grow="1" fullWidth relative>
              <StepComponent />

              <NewScanScheduleFooter onFinish={handleFinish} isSubmitting={isCreating} />
            </Box>
          </Box>
        </FullScreenModal>
      </NewScanScheduleContext.Provider>
    </FormProvider>
  );
};

export default NewScanSchedule;
